import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { GmdGapFlyout } from "@app/projects/network-assessment/gap-management-dashboard/components/GmdGapFlyout";
import { GapManagementDashboardService } from "@app/projects/network-assessment/gap-management-dashboard/gapManagementDashboardService";
import { getGapTypeBadge, getPriorityBadge, getFillableBadge } from "@app/projects/network-assessment/gap-management-dashboard/helpers/gmdGapBadges";
import { GmdGapRecords, GmdGapRecordsApiResponse } from "@app/projects/network-assessment/gap-management-dashboard/interfaces/gmdGapRecords";
import ProjectService from "@app/projects/projectService";
import { FlyoutLink } from "@app/shared/Flyouts";
import { formatDate } from "@app/shared/formatDateTime";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton, QESButtonTypeClasses } from "@app/shared/qes-standards/Button";
import { ColumnConfig, SortDirections, TextAlignment, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { QESWidget, QESWidgetContent, QESWidgetDescription, QESWidgetHeader, QESWidgetTitle } from "@app/shared/widget";
import { useQuery } from "@tanstack/react-query";
import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

const columnConfig: ColumnConfig = {
  contractedProviders: {
    alignment: TextAlignment.Right,
    displayText: "Contracted",
    testId: "contracted-providers",
  },
  county: {
    displayText: "County",
    testId: "county",
  },
  dateDue: {
    displayText: "Due Date",
    testId: "date-due",
  },
  dateUpdated: {
    displayText: "Updated",
    testId: "date-updated",
  },
  fillable: {
    displayText: "Fillable",
    testId: "fillable",
  },
  gapAgeDays: {
    displayText: "Gap Age",
    testId: "gap-age",
  },
  gapType: {
    displayText: "Gap Type",
    testId: "gap-type",
  },
  priority: {
    displayText: "Priority",
    testId: "priority",
  },
  resolutionStatus: {
    displayText: "Resolution Status",
    testId: "resolution-status",
  },
  specialty: {
    displayText: "Specialty",
    testId: "specialty",
  },
}

export const GapRecordsTable: FC = () => {
  const projectService = useRef(new ProjectService()).current;
  const gapManagementDashboardService = useRef(new GapManagementDashboardService()).current;
  const projectContext = useContext(CountyProjectContext);
  const lastSyncDate = useMemo(() => {
    const maxId = projectService.maxLastPublishedRunConfigurationId(projectContext.project, projectContext.runConfiguration.projectRunConfigurationType);
    const config = projectContext.project.projectRunConfigurations.find((prc) => prc.projectRunConfigurationId === maxId);
    if (!config) {
      return "---";
    }
    return formatDate(config.dateCreated);
  }, [projectContext.project, projectContext.runConfiguration.projectRunConfigurationType, projectContext.project.projectRunConfigurations]);
  const [gapRecordData, setGapRecordData] = useState<GmdGapRecords[]>([]);
  const [skip, setSkip] = useState(0);
  const [sortedColumn, setSortedColumn] = useState<keyof GmdGapRecords>(null);
  const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.None);
  const take = 50;

  const { data: gapRecordsResponse, isError, isLoading } = useQuery({
    enabled: !!projectContext.project.projectId && !!projectContext.runConfiguration.projectRunConfigurationId,
    queryFn: ({ signal }) => {
      const sortOptions = {
        column: sortedColumn as keyof GmdGapRecords,
        direction: sortDirection,
      };
      return gapManagementDashboardService.getGapRecordTableData(
        projectContext.project.projectId,
        projectContext.runConfiguration.projectRunConfigurationId,
        sortOptions,
        (skip / take),
        take,
        signal
      );
    },
    queryKey: ["gmd-gap-records", projectContext.runConfiguration.projectRunConfigurationId, sortedColumn, sortDirection, skip, take],
  });

  useEffect(() => {
    if (!!gapRecordsResponse) {
      setGapRecordData(handleGapRecordsResponseData(gapRecordsResponse));
    }
  }, [gapRecordsResponse]);

  const handleGapRecordsResponseData = (response: GmdGapRecordsApiResponse): GmdGapRecords[] => {
    return (response?.items || []).map<GmdGapRecords>((row) => {
      /* oxlint-disable sort-keys */
      // returned in table column order
      return {
        [wafflesKeyColumnName]: row.regionSetSpecialtyGroupId.toString(),
        county: (
          <FlyoutLink
            classNames={QESButtonTypeClasses.get("link")}
            content={{
              body: <GmdGapFlyout
                regionSetSpecialtyGroupId={row.regionSetSpecialtyGroupId}
                runConfigId={projectContext.runConfiguration.projectRunConfigurationId}
                projectId={projectContext.project.projectId}
                specialty={row.specialty}
              />,
              customHeader: true,
              title: "",

            }}
            testId={`gap-records__flyout--${row.regionSetSpecialtyGroupId}`}
            key={row.county}
            size="40"
          >
            {row.county}
          </FlyoutLink>
        ),
        specialty: row.specialty,
        gapType: <span key={row.gapType}>{getGapTypeBadge(row.gapType)}</span>,
        priority: <span key={row.priority}>{getPriorityBadge(row.priority)}</span>,
        fillable: <span key={row.fillable}>{getFillableBadge(row.fillable)}</span>,
        gapAgeDays: <span key={row.gapAgeDays}>{row.gapAgeDays.toLocaleString()} days</span>,
        contractedProviders: <span key={row.contractedProviders}>{row.contractedProviders.toLocaleString()}</span>,
      }
      /* oxlint-enable sort-keys */
    })
  }

  const handleSort = (records: GmdGapRecords[], column: keyof GmdGapRecords, direction: SortDirections) => {
    setSortedColumn(column);
    setSortDirection(direction);
    return records;
  }

  const paginationProps: WafflesPaginationProps = {
    changePage: (newPage: number) => { setSkip((newPage - 1) * take) },
    recordsPerPage: take,
    totalRecords: gapRecordsResponse?.totalCount ?? 0,
  }

  return (
    <QESWidget className="gap-management-dashboard__table-widget">
      <QESWidgetHeader>
        <QESWidgetTitle>
          <span>
            Gap Records
            {!!gapRecordsResponse ? <span className="gap-management-dashboard__table-subtitle">Showing {gapRecordData.length} of {gapRecordsResponse.totalCount} records &#183; Last synced {lastSyncDate}</span> : null}
          </span>
          <QESButton
            qesType="outline"
            qesTestId="gap-records-table__add-status"
            disabled
          >
            <QesIcon iconName={QesIcons.Plus} isDecorative style={{ marginRight: "4px", verticalAlign: "top" }} />Add Status
          </QESButton>
        </QESWidgetTitle>
        <QESWidgetDescription>
        </QESWidgetDescription>
      </QESWidgetHeader>
      <QESWidgetContent>
        {isError ? <QESAlert type="danger" persist={true}>We encountered an error loading this data.</QESAlert> : (
          <Waffles
            data={gapRecordData}
            columns={columnConfig}
            loading={isLoading}
            customSort={handleSort}
            testId="gap-records"
            fullWidth={true}
            paginationProps={paginationProps}
            enablePagination={true}
            passThroughSorting={true}
          />
        )}
      </QESWidgetContent>
    </QESWidget >
  );
}